// App header (JN5-0). Search (live) + account cluster. The balance chip shows
// the ACTIVE wallet's native GRAM balance, realtime off the wallet:{address}
// channel, and opens the Wallet popover (UJT-0) where the active wallet is
// switched. The avatar opens the account / Profile menu (UMX-0). The bell opens
// the Notifications popover (Y30-0) with a single unread badge.
//
// Deposit is a read-only view of the active wallet's non-bounceable address;
// it never needs or enables the signing/broadcast money path.
//
// GRAM is the summed live stream of selected wallets. USDT is the merged
// jetton holding of those same wallets — 0 once holdings have loaded.

import { BellIcon, CaretDownIcon, CheckIcon } from '@phosphor-icons/react'
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { formatHeaderTon } from '../../hooks/useLiveWalletBalance'
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss'
import { formatJettonHolding, formatRawExactAmount } from '../../lib/format'
import { accountAvatarSrc } from '../../lib/profileIdentity'
import { TETHER_USDT_MASTER } from '../../lib/searchDefault'
import { useAccountSession } from '../../wallet/AccountSessionContext'
import { useAuthShell } from '../../wallet/AuthShellContext'
import { useTradingSession } from '../../wallet/TradingSessionContext'
import { useActiveWallet } from '../../wallet/WalletsContext'
import { useGuestTradePrompt } from '../../wallet/guestTradePrompt'
import { TRADE_AUTH_REQUIRED_EVENT, hasPendingIdentityConfirmation } from '../../wallet/tradeAuth'
import { GuestAuthButtons } from '../auth/GuestAuthButtons'
import { Skeleton } from '../common/Skeleton'
import { ExchangeModal, type ExchangeTab } from '../exchange/ExchangeModal'
import { NotificationsPopover } from '../notifications/NotificationsPopover'
import { ProfilePopover } from '../profile/ProfilePopover'
import { SearchModal } from '../search/SearchModal'
import { useSearchBuy } from '../search/useSearchBuy'
import { SettingsModal } from '../settings/SettingsModal'
import { Described } from '../ui/Described'
import { GramMark } from '../ui/icons/GramMark'
import { UsdtMark } from '../ui/icons/UsdtMark'
import styles from './AppHeader.module.css'
import { HeaderNav } from './HeaderNav'
import { SearchInput } from './SearchInput'

// The wallet popover drags the whole vault/transfer/signing stack (@ton/core,
// @ton/crypto, @ton/ton — ~700KB minified). It renders only when the chip is
// open, so it loads as a lazy chunk on first open instead of gating app boot.
const WalletPopover = lazy(() =>
  import('../wallet/WalletPopover').then((module) => ({ default: module.WalletPopover }))
)

export function AppHeader() {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [walletOpen, setWalletOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const walletRef = useRef<HTMLDivElement>(null)
  const walletBtnRef = useRef<HTMLButtonElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)
  const profileBtnRef = useRef<HTMLButtonElement>(null)
  const notifRef = useRef<HTMLDivElement>(null)
  const notifBtnRef = useRef<HTMLButtonElement>(null)
  const [currency, setCurrency] = useState<'GRAM' | 'USDT'>('GRAM')
  const [currencyOpen, setCurrencyOpen] = useState(false)
  const currencyRef = useRef<HTMLDivElement>(null)
  const currencyBtnRef = useRef<HTMLButtonElement>(null)
  const [exchangeOpen, setExchangeOpen] = useState(false)
  const [exchangeTab, setExchangeTab] = useState<ExchangeTab>('deposit')

  const { status: accountStatus, me } = useAccountSession()
  const { prompt: promptGuestTrade } = useGuestTradePrompt()
  const { openAuth } = useAuthShell()
  const guest = accountStatus === 'guest'
  // a Search row's Buy is the lists' Buy (MONEY PATH: search/useSearchBuy)
  const searchBuy = useSearchBuy(searchOpen)
  const avatarSrc = accountAvatarSrc(me)
  const activeWallet = useActiveWallet()
  const { liveBalance, selectedBalanceNano, selectedPortfolio, portfolio, gramUsdRate } =
    useTradingSession()
  const balanceNano = guest ? null : (selectedBalanceNano ?? liveBalance.balanceNano)
  const usdtBalance = formatJettonHolding(
    (selectedPortfolio ?? portfolio.portfolio)?.holdings,
    TETHER_USDT_MASTER,
    6,
    activeWallet?.address
  )
  // A guest has no wallet, so there is nothing to measure and 0 is the honest
  // figure — not an unknown. Without this the chip shimmers forever, because
  // no measurement can ever arrive for a wallet that does not exist.
  // (MobileShell has always done this; the desktop header must agree.)
  const chipBalance = guest ? '0' : currency === 'GRAM' ? formatHeaderTon(balanceNano) : usdtBalance

  // Both header popovers dismiss on outside-click / Escape (Escape returns focus
  // to its trigger). Clicking one trigger while the other is open closes the
  // other via its own outside-click handler, so at most one is open at a time.
  usePopoverDismiss(walletRef, walletOpen, () => setWalletOpen(false), walletBtnRef)
  usePopoverDismiss(profileRef, profileOpen, () => setProfileOpen(false), profileBtnRef)
  usePopoverDismiss(notifRef, notifOpen, () => setNotifOpen(false), notifBtnRef)
  usePopoverDismiss(walletRef, currencyOpen, () => setCurrencyOpen(false), currencyBtnRef)

  useEffect(() => {
    const openTradeConfirmation = () => {
      setWalletOpen(false)
      setCurrencyOpen(false)
      setNotifOpen(false)
      setProfileOpen(false)
      if (guest) {
        promptGuestTrade()
        return
      }
      if (hasPendingIdentityConfirmation()) openAuth('confirm')
    }
    window.addEventListener(TRADE_AUTH_REQUIRED_EVENT, openTradeConfirmation)
    return () => window.removeEventListener(TRADE_AUTH_REQUIRED_EVENT, openTradeConfirmation)
  }, [guest, openAuth, promptGuestTrade])

  // Balance chip: the wallet+value zone opens the Wallet popover; the chevron
  // zone opens the GRAM/USDT currency switcher (UBZ-0/USE-0). Opening one closes
  // the other so at most one is shown.
  const openWallet = () => {
    setWalletOpen((o) => !o)
    setCurrencyOpen(false)
  }
  const openCurrency = () => {
    setCurrencyOpen((o) => !o)
    setWalletOpen(false)
  }
  const selectCurrency = (c: 'GRAM' | 'USDT') => {
    setCurrency(c)
    setCurrencyOpen(false)
  }

  // Exchange modal — opened on a specific tab from the header Deposit button or
  // the Wallet popover actions (which close the popover first).
  const openExchange = (t: ExchangeTab) => {
    setExchangeTab(t)
    setExchangeOpen(true)
    setWalletOpen(false)
  }

  return (
    <>
      <header className={styles.header}>
        {/* дизайн v8: слева лого + вкладки разделов; правый кластер —
            поиск → Deposit → кошелёк → колокольчик → аватар */}
        <HeaderNav />
        <div className={styles.spacer} />
        <SearchInput onOpen={() => setSearchOpen(true)} />

        {guest ? (
          <GuestAuthButtons className={styles.deposit} />
        ) : (
          <button type="button" className={styles.deposit} onClick={() => openExchange('deposit')}>
            Deposit
          </button>
        )}

        {!guest && (
          <div className={styles.walletWrap} ref={walletRef}>
            <div
              className={`${styles.balance} ${walletOpen || currencyOpen ? styles.balanceOpen : ''}`}
            >
              <button
                ref={walletBtnRef}
                type="button"
                className={styles.balanceMain}
                aria-haspopup="true"
                aria-expanded={walletOpen}
                aria-label={`${activeWallet ? `Wallet ${activeWallet.label}` : 'Wallet'}${
                  currency === 'GRAM' && balanceNano
                    ? `; exact balance ${formatRawExactAmount(balanceNano, 9)} GRAM`
                    : ''
                }`}
                onClick={openWallet}
              >
                <span className={styles.balanceAmt}>
                  {currency === 'GRAM' ? <GramMark size={16} /> : <UsdtMark size={16} />}
                  <Described
                    as="span"
                    className={styles.balanceValue}
                    data-testid="header-balance"
                    description={
                      currency === 'GRAM' && balanceNano
                        ? `Exact balance ${formatRawExactAmount(balanceNano, 9)} GRAM`
                        : undefined
                    }
                  >
                    {chipBalance === '' ? <Skeleton width={40} height={11} /> : chipBalance}
                  </Described>
                </span>
              </button>
              <button
                ref={currencyBtnRef}
                type="button"
                className={`${styles.balanceChevron} ${currencyOpen ? styles.balanceChevronOpen : ''}`}
                aria-haspopup="true"
                aria-expanded={currencyOpen}
                aria-label="Switch balance currency"
                onClick={openCurrency}
              >
                <CaretDownIcon size={14} weight="bold" />
              </button>
            </div>
            {walletOpen && (
              <Suspense fallback={null}>
                <WalletPopover
                  onAction={openExchange}
                  balanceNano={balanceNano}
                  gramUsdRate={gramUsdRate}
                  onClose={() => setWalletOpen(false)}
                />
              </Suspense>
            )}
            {currencyOpen && (
              <div className={styles.currencyMenu} ref={currencyRef}>
                {(['GRAM', 'USDT'] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`${styles.currencyOption} ${c === currency ? styles.currencyOptionActive : ''}`}
                    aria-pressed={c === currency}
                    onClick={() => selectCurrency(c)}
                  >
                    <span className={styles.currencyOptionLeft}>
                      {c === 'GRAM' ? <GramMark size={18} /> : <UsdtMark size={18} />}
                      <span className={styles.currencyLabel}>{c}</span>
                    </span>
                    {c === currency && (
                      <CheckIcon size={15} weight="bold" className={styles.currencyCheck} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!guest && (
          <div className={styles.notifWrap} ref={notifRef}>
            <button
              ref={notifBtnRef}
              type="button"
              className={styles.bell}
              aria-haspopup="true"
              aria-expanded={notifOpen}
              aria-label="Notifications"
              onClick={() => setNotifOpen((o) => !o)}
            >
              <BellIcon size={18} weight="bold" />
              {/* бейдж непрочитанных вернётся с бэкендом нотификаций */}
            </button>
            {notifOpen && (
              <NotificationsPopover
                onClose={() => setNotifOpen(false)}
                onOpenSettings={() => {
                  setNotifOpen(false)
                  setSettingsOpen(true)
                }}
              />
            )}
          </div>
        )}

        {!guest && (
          <div className={styles.profileWrap} ref={profileRef}>
            <button
              ref={profileBtnRef}
              type="button"
              className={styles.avatar}
              aria-haspopup="true"
              aria-expanded={profileOpen}
              aria-label="Account"
              onClick={() => setProfileOpen((o) => !o)}
            >
              {avatarSrc ? <img src={avatarSrc} alt="" /> : null}
            </button>
            {profileOpen && (
              <ProfilePopover
                onClose={() => setProfileOpen(false)}
                onOpenSettings={() => setSettingsOpen(true)}
              />
            )}
          </div>
        )}
      </header>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} {...searchBuy} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <ExchangeModal
        open={exchangeOpen}
        tab={exchangeTab}
        gramUsdRate={gramUsdRate}
        balances={{ gramNano: balanceNano, usdt: usdtBalance }}
        onClose={() => setExchangeOpen(false)}
      />
    </>
  )
}

export function shouldLoadHeaderGramUsdRate(
  balanceNano: string | null,
  walletOpen: boolean,
  exchangeOpen: boolean
): boolean {
  return balanceNano !== null && (walletOpen || exchangeOpen)
}
